import { useMemo } from 'react'

import { useMentionsCounterMode } from '~publish/pages/Community/hooks/mentions/useMentionsCounterMode'

import { useCommunityCountsContext } from './CommunityCountsContext'

export type CommunityCounter = {
  /**
   * The effective general Community counter: unanswered comments, plus
   * unanswered mentions when mentions are enabled and in `counter` mode.
   * In `dot` mode (or when mentions are disabled) mentions are excluded.
   */
  total: number
  /**
   * Per-channel equivalent of {@link total}: unanswered comments for the
   * channel, plus its unanswered mentions when {@link includesMentions}.
   */
  getChannelTotal: (channelId: string) => number
  /** Whether mentions are contributing to the totals. */
  includesMentions: boolean
  loading: boolean
}

/**
 * Applies the mentions counter mode on top of the raw counts to produce the
 * numbers shown in the Community counters (left-nav aggregate badge and the
 * per-channel sidebar badge). Kept separate from `CommunityCountsContext`,
 * which stays a mode-agnostic data layer.
 */
export function useCommunityCounter(): CommunityCounter {
  const {
    totalOrgCount,
    totalOrgMentionCount,
    getChannelCount,
    getChannelMentionCount,
    mentionsEnabled,
    loading,
  } = useCommunityCountsContext()
  const { mode } = useMentionsCounterMode()

  return useMemo(() => {
    const includesMentions = mentionsEnabled && mode === 'counter'
    return {
      total: totalOrgCount + (includesMentions ? totalOrgMentionCount : 0),
      getChannelTotal: (channelId: string): number => {
        const comments = getChannelCount(channelId) ?? 0
        const mentions = includesMentions
          ? (getChannelMentionCount(channelId) ?? 0)
          : 0
        return comments + mentions
      },
      includesMentions,
      loading,
    }
  }, [
    totalOrgCount,
    totalOrgMentionCount,
    getChannelCount,
    getChannelMentionCount,
    mentionsEnabled,
    mode,
    loading,
  ])
}
